import { Dimension, DimensionValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { ElementsGroupByPosition } from "../context";
import { Property, SsqSsqHeadingSsqProperty } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { PostElement } from "src/api/client-api/post/postSchema";
import { overrideProperty } from "./overrideProperty";

type Options = {
    dimensionValue: DimensionValue | undefined;
    childProperties: Property[];
    elementsMapping: ElementsGroupByPosition;
    ssqSsqHeadingSsqProperty: SsqSsqHeadingSsqProperty;
    ssqVersionStructureId?: number | null;
    ingredientGenericValues: Partial<Record<number, string>>;
};

export const computeElementsGroup = (options: Options): MultiPropertyGroup[] => {
    const {
        dimensionValue,
        childProperties,
        elementsMapping,
        ssqSsqHeadingSsqProperty,
        ssqVersionStructureId,
        ingredientGenericValues,
    } = options;

    const multivalueDimension = OlapScoresService.getDimension("multivalues", dimensionValue?.dimensions);

    const allGroups = Object.entries(elementsMapping).flatMap(([position, elements]) => {
        if (elements === undefined) return [];

        // I'm unsure we should really do `position + 1` ?
        // But I think I've seen it in the past... that `position in OLAP` is `position + 1`
        const positionNumber = parseInt(position);
        const olapPosition = positionNumber + 1;
        const multivalueScore = OlapScoresService.getDimensionValue(olapPosition, multivalueDimension);
        // const multivalueLabel = OlapScoresService.normalizeNumericalScore(multivalueScore?.score.value);
        const multivalueLabel = 0;

        const subpropertyDimension = OlapScoresService.getDimension("subproperties", multivalueScore?.dimensions);
        const nbOfScores = subpropertyDimension?.values.length ?? 0;

        // If only one score on the Multivalue Group of elements, then, only display the total
        const isScoreDisplayable = multivalueLabel !== null && multivalueLabel !== 0;
        const hasMultipleSubscore = nbOfScores > 1;
        const shouldDisplayScore = isScoreDisplayable && hasMultipleSubscore;

        // I think this is the exact same purpose of shouldDisplayScore above;
        // idk what that is, but for the sake of reproducing...
        // let subpropertyNbOfScores = 0;

        const propertiesData = childProperties.map((cP) => {
            const subpropertyScore = OlapScoresService.getPropertyDimensionValue(
                cP.id,
                ssqVersionStructureId,
                subpropertyDimension,
            );
            const scoreLabel = OlapScoresService.normalizeNumericalScore(subpropertyScore?.score.value);
            // if (scoreLabel !== null) {
            //     subpropertyNbOfScores += 1;
            // }

            const foundElem = elements.find((e) => {
                return cP.id === e.ssqPropertyId;
            });

            const override = ssqSsqHeadingSsqProperty.children?.find((v) => v.id === cP.id);
            const overridenProperty = overrideProperty({
                originalProperty: cP,
                potentialOverrides: override,
                ingredientGenericValue: ingredientGenericValues[cP.id],
            });
            const multiPropertyData: MultiPropertyData = {
                property: overridenProperty,
                postElement: foundElem,
                subpropertyScore: subpropertyScore,
                score: scoreLabel,
            };
            return multiPropertyData;
        });

        const multiPropertyGroup: MultiPropertyGroup = {
            propertiesData,
            subpropertyDimension,
            position: positionNumber,
            multivalueLabel,
            nbOfScores,
            shouldDisplayScore,
        };
        return multiPropertyGroup;
    });

    return allGroups;
};

export type MultiPropertyGroup = {
    propertiesData: MultiPropertyData[];
    subpropertyDimension: Dimension | undefined;
    position: number;
    multivalueLabel: number | null;
    nbOfScores: number;
    shouldDisplayScore: boolean;
};

export type MultiPropertyData = {
    property: Property;
    score: number | null;
    subpropertyScore: DimensionValue | undefined;
    postElement: PostElement | undefined;
};
